import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { cn, css } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { getAdaptedBaseCardContent } from '@/modules/content/card/utils/getAdaptedBaseCardContent';
import * as BaseCard from '@/modules/content/components/ContentCard/BaseCard';
import {
  cardSizeMobileStyles,
  cardSizeStyles,
  COMMON_CARD_CLASSNAME,
} from '@/modules/content/components/ContentCard/styles';
import { ContentCardTypeEnum } from '@/modules/content/config/cards';

import { renderProgressStatus } from './renderers';
import { imageContainerStyles, imageMobileStyles, imageStyles, miniCardSizeStyles } from './styles';
import { MiniCardProps } from './types';

const MiniCard: React.FC<MiniCardProps> = (props) => {
  const { isMobile } = useScreen();
  const {
    content: contentProp,
    baseContent,
    className,
    imageProps,
    disabled,
    noDescription,
  } = props;
  const { t } = useTranslation();

  const content = baseContent ?? getAdaptedBaseCardContent(t, contentProp);
  const { progress } = content;

  const pillsSection = (
    <Stack className="mt-auto" direction="row" gap={7}>
      <BaseCard.Status />
      {baseContent ? null : renderProgressStatus(t, progress)}
    </Stack>
  );

  const overrideDefaultStyle =
    noDescription && !isMobile
      ? cn(
          cardSizeStyles({
            height: 16.8,
            width: 37.6,
          }),
          cardSizeMobileStyles({
            height: 11.2,
            width: 36.4,
          }),
        )
      : undefined;

  const overrideDefaultImageStyle = noDescription
    ? css`
        width: ${10.4}rem;
        height: ${14.2}rem;
      `
    : undefined;

  const overrideDefaultMobileImageStyle = noDescription
    ? css`
        width: ${6.4}rem;
        height: ${10.8}rem;
      `
    : undefined;

  const height = imageProps.height + (noDescription && !isMobile ? 26 : 0);

  return (
    <BaseCard.Provider
      // when the padding is change, change the MINI_CARD_PADDING
      cardType={ContentCardTypeEnum.Mini}
      className={cn(
        COMMON_CARD_CLASSNAME,
        overrideDefaultStyle || miniCardSizeStyles,
        className,
        'relative p-[18px]',
      )}
      content={content}
      disabled={disabled}>
      <Stack className="w-full h-full" direction="col" gap={12}>
        <Stack className="w-full h-full" direction="row">
          <BaseCard.Image
            alt="content image"
            className={cn(
              isMobile
                ? overrideDefaultMobileImageStyle || imageMobileStyles
                : overrideDefaultImageStyle || imageStyles,
            )}
            containerClassName={cn('flex-shrink-0', imageContainerStyles)}
            height={height}
            width={imageProps.width}
          />
          <Stack className="flex-shrink flex-grow ms-12" direction="col">
            <BaseCard.Label />
            <BaseCard.Name className="mt-4 flex-shrink" numberOfLines={2} />
            {isMobile ? null : pillsSection}
          </Stack>
        </Stack>
        {isMobile ? pillsSection : null}
      </Stack>
    </BaseCard.Provider>
  );
};

export default React.memo(MiniCard);
